import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Eye, User, Share2, Facebook, Twitter, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getNewsArticleBySlug, trackNewsView, Article, getImageUrl, formatDate, formatReadingTime } from '@/lib/api';
import { useMetadata } from '@/hooks/useMetadata';

const NewsDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState('');

  useMetadata({
    title: article?.seo_title || article?.title,
    description: article?.seo_description || article?.excerpt,
    keywords: article?.seo_keywords,
    ogImage: article?.featured_image ? getImageUrl(article.featured_image) : undefined,
    canonicalUrl: article ? `https://mindfulme.pusatandalan.com/news/${article.slug}` : undefined,
  });

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
      setShareUrl(window.location.href);
    }
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    setLoading(true);
    setError(null);

    try {
      const articleData = await getNewsArticleBySlug(articleSlug);
      setArticle(articleData);

      // Track view after a short delay to ensure user engagement
      setTimeout(() => {
        trackNewsView(articleData.id);
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Artikel tidak ditemukan';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform: string) => {
    const url = shareUrl;
    const title = article?.title || '';
    let shareLink = '';

    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
        break;
      default:
        return;
    }

    window.open(shareLink, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link berhasil disalin!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-gray-600">Memuat artikel...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/news" className="flex items-center text-blue-600 hover:text-blue-700 mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali ke Berita
          </Link>

          <Alert variant="destructive">
            <AlertDescription>
              {error || 'Artikel tidak ditemukan'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link to="/news" className="flex items-center text-blue-600 hover:text-blue-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Kembali ke Berita
        </Link>

        {/* Article Content */}
        <article>
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary">{article.category?.name || 'Artikel'}</Badge>
              {article.published_at && (
                <Badge variant="outline" className="text-green-600">
                  Diterbitkan
                </Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              {article.excerpt}
            </p>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{article.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(article.published_at || article.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formatReadingTime(article.read_time_minutes)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span>{article.view_count} dilihat</span>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          {article.featured_image && (
            <div className="mb-8">
              <img
                src={getImageUrl(article.featured_image)}
                alt={article.title}
                className="w-full h-[300px] md:h-[450px] object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>
          )}

          {/* Article Body */}
          <div className="prose prose-lg max-w-none mb-8">
            <div
              dangerouslySetInnerHTML={{
                __html: article.content,
              }}
              className="text-gray-700 leading-relaxed"
            />
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Badge key={tag.id} variant="outline" className="text-sm">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Share Section */}
          <div className="border-t pt-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Bagikan Artikel</h3>
                <p className="text-gray-600 text-sm">
                  Bagikan artikel ini kepada teman dan keluarga
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('facebook')}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('twitter')}
                  className="text-sky-500 hover:text-sky-600"
                >
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('whatsapp')}
                  className="text-green-600 hover:text-green-700"
                >
                  <MessageCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </article>

        {/* Author Info */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle className="text-lg">Tentang Penulis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{article.author.name}</h4>
                <p className="text-gray-600 text-sm mb-2">{article.author.email}</p>
                {article.author.bio && (
                  <p className="text-gray-700 text-sm">{article.author.bio}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SEO Meta (for development/debugging) */}
        {import.meta.env.DEV && (
          <details className="mt-8 border rounded-lg p-4">
            <summary className="cursor-pointer font-medium mb-2">SEO Information (Debug)</summary>
            <div className="space-y-2 text-sm">
              <div><strong>Title:</strong> {article.seo_title || article.title}</div>
              <div><strong>Description:</strong> {article.seo_description || article.excerpt}</div>
              <div><strong>Keywords:</strong> {article.seo_keywords || 'N/A'}</div>
              <div><strong>Slug:</strong> {article.slug}</div>
              <div><strong>ID:</strong> {article.id}</div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default NewsDetail;